import { useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	Box,
	Alert,
	Grid,
	IconButton,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { admissionMethodApi, type AdmissionMethod } from '../../services';
import { useAppSelector } from '../../store/hooks';
import { DIALOG_SETTINGS, FULLSCREEN_DIALOG_PAPER_SX, FULLSCREEN_DIALOG_CONTENT_SX } from '../../utils/responsive';
import { useResponsiveDialog, useEntityDialog } from './hooks';
import { DialogFormActions, StatisticsPanel } from './base';
import { type AdmissionMethodFormData } from '../../schemas/forms';

interface AdmissionMethodDialogProps {
	open: boolean;
	onClose: () => void;
	admissionMethod?: AdmissionMethod | null;
	mode: 'create' | 'edit' | 'view';
	maNganh?: string | null;
}

interface FormErrors {
	maPhuongThuc?: string;
	tenPhuongThuc?: string;
	doUuTien?: string;
}

export default function AdmissionMethodDialog({
	open,
	onClose,
	admissionMethod,
	mode,
	maNganh,
}: AdmissionMethodDialogProps) {
	// Responsive and mode helpers
	const { isMobile, isReadOnly, isEditMode } = useResponsiveDialog({ mode });
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);

	const [formData, setFormData] = useState<AdmissionMethodFormData>({
		maPhuongThuc: '',
		tenPhuongThuc: '',
		doUuTien: 0,
	});

	const [errors, setErrors] = useState<FormErrors>({});

	// Entity mutations
	const { isLoading, handleCreate, handleUpdate, createMutation, updateMutation } = useEntityDialog<
		{ maPhuongThuc: string; tenPhuongThuc: string; doUuTien: number },
		{ tenPhuongThuc: string; doUuTien: number }
	>({
		entityName: 'phương thức tuyển sinh',
		queryKeys: [['admissionMethods']],
		createFn: admissionMethodApi.createAdmissionMethod,
		updateFn: (id, data) => admissionMethodApi.updateAdmissionMethod(id, data),
		onSuccess: onClose,
	});

	// Fetch admission method statistics for view/edit mode (filtered by faculty/major if selected)
	const { data: methodStatsData, isLoading: isLoadingStats } = useQuery({
		queryKey: ['admissionMethod', 'stats', admissionMethod?.maPhuongThuc, selectedFaculty, maNganh],
		queryFn: async () => {
			if (!admissionMethod?.maPhuongThuc) return null;
			const params = new URLSearchParams();
			params.set('maPhuongThuc', admissionMethod.maPhuongThuc);
			params.set('limit', '1');
			if (selectedFaculty) {
				params.set('maKhoa', selectedFaculty);
			}
			if (maNganh) {
				params.set('maNganh', maNganh);
			}
			const response = await admissionMethodApi.getAdmissionMethodStats(params);
			return response.data?.stats?.[0] || null;
		},
		enabled: open && !!admissionMethod?.maPhuongThuc && (mode === 'view' || mode === 'edit'),
	});

	// Initialize form data when dialog opens
	useEffect(() => {
		if (open) {
			if ((mode === 'edit' || mode === 'view') && admissionMethod) {
				setFormData({
					maPhuongThuc: admissionMethod.maPhuongThuc,
					tenPhuongThuc: admissionMethod.tenPhuongThuc || '',
					doUuTien: admissionMethod.doUuTien,
				});
			} else {
				setFormData({
					maPhuongThuc: '',
					tenPhuongThuc: '',
					doUuTien: 0,
				});
			}
			setErrors({});
		}
	}, [open, mode, admissionMethod]);

	const validateForm = (): boolean => {
		const newErrors: FormErrors = {};

		if (!formData.maPhuongThuc.trim()) {
			newErrors.maPhuongThuc = 'Mã phương thức là bắt buộc';
		}

		if (!formData.tenPhuongThuc.trim()) {
			newErrors.tenPhuongThuc = 'Tên phương thức là bắt buộc';
		}

		if (formData.doUuTien < 0) {
			newErrors.doUuTien = 'Độ ưu tiên không được âm';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (isReadOnly) return;

		if (!validateForm()) {
			return;
		}

		if (mode === 'create') {
			handleCreate({
				maPhuongThuc: formData.maPhuongThuc.trim(),
				tenPhuongThuc: formData.tenPhuongThuc.trim(),
				doUuTien: formData.doUuTien,
			});
		} else if (admissionMethod) {
			handleUpdate(admissionMethod.maPhuongThuc, {
				tenPhuongThuc: formData.tenPhuongThuc.trim(),
				doUuTien: formData.doUuTien,
			});
		}
	};

	const handleInputChange = (field: keyof AdmissionMethodFormData, value: string | number) => {
		setFormData(prev => ({
			...prev,
			[field]: value,
		}));

		// Clear error when user starts typing
		if (errors[field]) {
			setErrors(prev => ({
				...prev,
				[field]: undefined,
			}));
		}
	};

	const getDialogTitle = () => {
		switch (mode) {
			case 'create':
				return 'Tạo phương thức tuyển sinh mới';
			case 'edit':
				return 'Chỉnh sửa phương thức tuyển sinh';
			case 'view':
				return 'Chi tiết phương thức tuyển sinh';
		}
	};

	// View mode render
	if (isReadOnly) {
		return (
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth={DIALOG_SETTINGS.FORM.MAX_WIDTH}
				fullWidth
				fullScreen={isMobile}
				PaperProps={{
					sx: isMobile ? FULLSCREEN_DIALOG_PAPER_SX : { p: DIALOG_SETTINGS.TABLET_PADDING },
				}}
			>
				<DialogTitle sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
					{isMobile && (
						<IconButton aria-label="back" onClick={onClose} edge="start" sx={{ mr: 1 }}>
							<ArrowBackIcon />
						</IconButton>
					)}
					{getDialogTitle()}
				</DialogTitle>
				<DialogContent sx={isMobile ? FULLSCREEN_DIALOG_CONTENT_SX : undefined}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
						<TextField
							label="Mã phương thức"
							value={formData.maPhuongThuc}
							fullWidth
							slotProps={{ input: { readOnly: true } }}
						/>
						<TextField
							label="Tên phương thức"
							value={formData.tenPhuongThuc}
							fullWidth
							slotProps={{ input: { readOnly: true } }}
						/>
						<TextField
							label="Độ ưu tiên"
							value={formData.doUuTien}
							fullWidth
							slotProps={{ input: { readOnly: true } }}
						/>
						{methodStatsData && (
							<StatisticsPanel
								yearData={methodStatsData.yearData || {}}
								isLoading={isLoadingStats}
							/>
						)}
					</Box>
				</DialogContent>
				<DialogFormActions
					mode={mode}
					isLoading={isLoading}
					isReadOnly={isReadOnly}
					isMobile={isMobile}
					onClose={onClose}
				/>
			</Dialog>
		);
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={DIALOG_SETTINGS.FORM.MAX_WIDTH}
			fullWidth
			fullScreen={isMobile}
			PaperProps={{
				component: 'form',
				onSubmit: handleSubmit,
				sx: isMobile ? FULLSCREEN_DIALOG_PAPER_SX : { p: DIALOG_SETTINGS.TABLET_PADDING },
			}}
		>
			<DialogTitle sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
				{isMobile && (
					<IconButton
						aria-label="back"
						onClick={onClose}
						edge="start"
						disabled={isLoading}
						sx={{ mr: 1 }}
					>
						<ArrowBackIcon />
					</IconButton>
				)}
				{getDialogTitle()}
			</DialogTitle>

			<DialogContent sx={isMobile ? FULLSCREEN_DIALOG_CONTENT_SX : undefined}>
				<Box sx={{ mt: 2 }}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Mã phương thức"
								value={formData.maPhuongThuc}
								onChange={(e) => handleInputChange('maPhuongThuc', e.target.value)}
								error={!!errors.maPhuongThuc}
								helperText={errors.maPhuongThuc}
								disabled={isEditMode}
								required
							/>
						</Grid>

						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Tên phương thức"
								value={formData.tenPhuongThuc}
								onChange={(e) => handleInputChange('tenPhuongThuc', e.target.value)}
								error={!!errors.tenPhuongThuc}
								helperText={errors.tenPhuongThuc}
								required
							/>
						</Grid>

						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Độ ưu tiên"
								type="number"
								value={formData.doUuTien}
								onChange={(e) => handleInputChange('doUuTien', parseInt(e.target.value) || 0)}
								error={!!errors.doUuTien}
								helperText={errors.doUuTien || 'Số càng cao thì ưu tiên càng lớn'}
								slotProps={{
									htmlInput: {
										min: 0,
									}
								}}
							/>
						</Grid>
					</Grid>

					{isEditMode && methodStatsData && (
						<StatisticsPanel
							yearData={methodStatsData.yearData || {}}
							isLoading={isLoadingStats}
						/>
					)}

					{(createMutation.isError || updateMutation.isError) && (
						<Alert severity="error" sx={{ mt: 2 }}>
							{'Không thể lưu phương thức tuyển sinh. Vui lòng kiểm tra lại thông tin và thử lại.'}
						</Alert>
					)}
				</Box>
			</DialogContent>

			<DialogFormActions
				mode={mode}
				isLoading={isLoading}
				isReadOnly={isReadOnly}
				isMobile={isMobile}
				onClose={onClose}
				labels={{ create: 'Tạo' }}
			/>
		</Dialog>
	);
}